import { useCallback } from 'react'

import {
  Currency,
  extractValidationErrors,
  isSequenceApiError,
  useMutation
} from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'

import {
  ApplyCreditRow,
  amountToRequestValue,
  parseAmount
} from './applyCreditNote.helpers'

type Balance = Dashboardv99990101Api.PostCreditNoteApplication.Balance
type VersionConflictResponse =
  Dashboardv99990101Api.PostCreditNoteApplication.VersionConflictResponse

export type ApplyCreditFailure =
  | { kind: 'conflict'; rowId: string; conflict: VersionConflictResponse }
  | { kind: 'error'; message: string }

export type ApplyCreditsResult = {
  succeeded: ApplyCreditRow[]
  /** The first row that failed, if any. Rows after it are left untouched. */
  failed: ApplyCreditFailure | null
  /** Credit note balance quoted by the last response, for the next attempt. */
  creditNoteBalance: Balance | null
}

/**
 * Applies a credit note to one invoice per call, sequentially, threading the
 * balance version through each response. A failure stops the loop — the
 * threaded version is stale afterwards, so later rows would fail too.
 */
export const useApplyCreditNote = ({
  creditNoteId,
  currency
}: {
  creditNoteId: string
  currency: Currency
}) => {
  const { mutateAsync: postCreditNoteApplication } = useMutation(
    dashboardv99990101Client.postCreditNoteApplication
  )

  const applyCredits = useCallback(
    ({
      rows,
      creditNoteBalanceVersion
    }: {
      rows: ApplyCreditRow[]
      creditNoteBalanceVersion: number
    }): Promise<ApplyCreditsResult> => {
      const applyRow = async (
        accumulated: Promise<ApplyCreditsResult>,
        row: ApplyCreditRow
      ): Promise<ApplyCreditsResult> => {
        const acc = await accumulated

        if (acc.failed) return acc

        const body = {
          creditNoteId,
          invoiceId: row.invoiceId,
          // The 2dp amount validation and the totals used, not raw input.
          amount: amountToRequestValue(parseAmount(row.amount) ?? row.amount),
          currency,
          creditNoteBalanceVersion:
            acc.creditNoteBalance?.version ?? creditNoteBalanceVersion,
          invoiceBalanceVersion: row.invoiceBalanceVersion
        }

        try {
          const response = await postCreditNoteApplication({ body })

          return {
            ...acc,
            succeeded: [...acc.succeeded, row],
            creditNoteBalance:
              response?.creditNoteBalance ?? acc.creditNoteBalance
          }
        } catch (error) {
          return { ...acc, failed: toFailure(row.rowId, error) }
        }
      }

      // Awaiting the accumulator is what serialises the calls: every request
      // needs the balance version the previous response quoted.
      return rows.reduce(applyRow, Promise.resolve(NOTHING_APPLIED))
    },
    [creditNoteId, currency, postCreditNoteApplication]
  )

  return { applyCredits }
}

const NOTHING_APPLIED: ApplyCreditsResult = {
  succeeded: [],
  failed: null,
  creditNoteBalance: null
}

const toFailure = (rowId: string, error: unknown): ApplyCreditFailure => {
  const conflict =
    isSequenceApiError(error) && error.status === 409
      ? asVersionConflict(error.response)
      : null

  if (conflict) {
    return { kind: 'conflict', rowId, conflict }
  }

  return { kind: 'error', message: extractValidationErrors(error).at(0) ?? '' }
}

/** The 409 body isn't one of the shared error shapes, so it's narrowed here. */
const asVersionConflict = (
  response: unknown
): VersionConflictResponse | null =>
  response !== null &&
  typeof response === 'object' &&
  'currentInvoiceBalance' in response &&
  'currentCreditNoteBalance' in response
    ? (response as VersionConflictResponse)
    : null
